import type {
  PlatformFile,
  UploadFlow,
  UploadItemKind,
  UploadStatus,
  EditorImportRuntimeState,
  EditorImportAdmission,
} from "../../types";
import { EDITOR_RESTORE_STATE_STORAGE_KEY } from "./restoreSession";

export type EditorRestoreSource = "route" | "session" | "merged";

export interface EditorLocationState {
  editorOpenTimingId?: string;
  projectId?: string;
  storageMode?: "legacy" | "revision";
  editHeadRevisionId?: string | null;
  viewRCreated?: boolean;
  fromRecentFile?: boolean;
  error?: string;
  isSampleFile?: boolean;
  sampleName?: string;
  gcsPath?: string;
  gcs_path?: string;
  title?: string;
  fileName?: string;
  jobId?: string;
  createdAt?: string;
  downloadUrl?: string;
  convertedDownloadUrl?: string;
  fileSize?: number;
  fromSSEEvent?: boolean;
  platformFiles?: unknown[];
  viewrGCSPath?: string;
  fullPath?: string;
  restoreUploadItemId?: string;
  restoreTargetSpaceIndex?: number | null;
  restoreTargetSpaceName?: string | null;
  restoreAfterUploadComplete?: boolean;
  restoreRequestId?: string;
  __restoreSource?: EditorRestoreSource;
  restoreItemSnapshot?: {
    editorImportAdmission?: EditorImportAdmission | null;
    id: string;
    fileName: string;
    fileUrl?: string | null;
    gcsPath?: string | null;
    convertedDownloadUrl?: string | null;
    jobId?: string | null;
    platformFiles?: PlatformFile[] | null;
    kind?: UploadItemKind;
    flow?: UploadFlow | null;
    status?: UploadStatus;
    fileData?: Record<string, unknown> | null;
    originViewrGcsPath?: string | null;
    originViewrTitle?: string | null;
    originSpaceIndex?: number | null;
    targetSpaceIndex?: number | null;
    targetSpaceName?: string | null;
    editorImportVseIndex?: number | null;
    editorImportRuntimeState?: EditorImportRuntimeState | null;
  };
}

const EDITOR_RESTORE_LOCATION_KEYS: Array<keyof EditorLocationState> = [
  "restoreUploadItemId",
  "restoreTargetSpaceIndex",
  "restoreTargetSpaceName",
  "restoreAfterUploadComplete",
  "restoreRequestId",
  "restoreItemSnapshot",
  "__restoreSource",
];

export const stripEditorRestoreLocationState = (
  state: EditorLocationState | null | undefined,
): EditorLocationState | null | undefined => {
  if (!state) return state;

  const nextState = { ...state };
  EDITOR_RESTORE_LOCATION_KEYS.forEach((key) => {
    delete nextState[key];
  });
  return nextState;
};

/**
 * Browser reload 전에 React Router의 history.state에서 active restore intent를
 * 동기적으로 제거해 같은 request가 다시 실행되지 않도록 한다.
 */
export const clearEditorRestoreBrowserHistoryState = (
  requestId?: string | null,
): void => {
  if (typeof window === "undefined") return;

  const historyState = window.history.state as
    | { usr?: EditorLocationState | null; [key: string]: unknown }
    | EditorLocationState
    | null;
  if (!historyState || typeof historyState !== "object") return;

  if ("usr" in historyState) {
    const routeState = historyState.usr;
    if (
      requestId &&
      routeState?.restoreRequestId &&
      routeState.restoreRequestId !== requestId
    ) {
      return;
    }
    window.history.replaceState(
      {
        ...historyState,
        usr: stripEditorRestoreLocationState(routeState) ?? null,
      },
      "",
    );
    return;
  }

  const routeState = historyState as EditorLocationState;
  if (
    requestId &&
    routeState.restoreRequestId &&
    routeState.restoreRequestId !== requestId
  ) {
    return;
  }
  window.history.replaceState(
    stripEditorRestoreLocationState(routeState) ?? null,
    "",
  );
};

export const readPersistedEditorRestoreState =
  (): EditorLocationState | null => {
    if (typeof window === "undefined") return null;

    const raw = sessionStorage.getItem(EDITOR_RESTORE_STATE_STORAGE_KEY);
    if (!raw) return null;

    try {
      return JSON.parse(raw) as EditorLocationState;
    } catch {
      sessionStorage.removeItem(EDITOR_RESTORE_STATE_STORAGE_KEY);
      return null;
    }
  };

export const resolveEditorRestoreStateSource = (
  routeState: EditorLocationState | null | undefined,
  persistedState: EditorLocationState | null | undefined,
): EditorRestoreSource | null => {
  const hasPersisted = persistedState?.restoreAfterUploadComplete === true;
  const hasRoute = routeState?.restoreAfterUploadComplete === true;

  if (hasPersisted && hasRoute) return "merged";
  if (hasRoute) return "route";
  if (hasPersisted) return "session";
  return null;
};

/** Aligns toast restore with `createRestoreMachine` (canonical target before origin). */
export const resolveEditorUploadRestoreTargetSpaceIndex = (item: {
  targetSpaceIndex?: number | null;
  originSpaceIndex?: number | null;
}): number | null => {
  if (
    typeof item.targetSpaceIndex === "number" &&
    Number.isFinite(item.targetSpaceIndex) &&
    item.targetSpaceIndex >= 0
  ) {
    return Math.trunc(item.targetSpaceIndex);
  }
  if (
    typeof item.originSpaceIndex === "number" &&
    Number.isFinite(item.originSpaceIndex) &&
    item.originSpaceIndex >= 0
  ) {
    return Math.trunc(item.originSpaceIndex);
  }
  return null;
};

export const mergeEditorLocationState = (
  routeState: EditorLocationState | null | undefined,
  persistedState: EditorLocationState | null | undefined,
): EditorLocationState | null | undefined => {
  const persisted =
    persistedState?.restoreAfterUploadComplete === true ? persistedState : null;

  if (!routeState) {
    return persisted
      ? {
          ...persisted,
          __restoreSource: "session",
        }
      : persisted;
  }

  if (!persisted) {
    return routeState.restoreAfterUploadComplete
      ? {
          ...routeState,
          __restoreSource: "route",
        }
      : routeState;
  }

  if (routeState.restoreAfterUploadComplete) {
    return {
      ...persisted,
      ...routeState,
      __restoreSource: "merged",
    };
  }

  return routeState;
};
